import React, { ReactNode, useContext, useEffect, useState } from "react";
import Slider from "react-slick";
import cx from "classnames";
import { HomepageHeroSlide } from "components/HomepageHeroSlide";
import { HomepageHeroSlideRecord } from "services/datocms/generated";
import st from "./index.module.scss";
import { logInternalPromotionView } from "@pepdirect/helpers/analyticsLogger";
import {
  ItemByGtinDocument,
  ItemByGtinQuery,
  ItemByGtinQueryVariables,
} from "services/graphql/generated";
import client from "services/graphql/client";
import { Product } from "@pepdirect/shared/types";
import { LogContext } from "context/log";

export interface HomepageHeroSlidesProps {
  slides: (HomepageHeroSlideRecord & {
    primaryGtin?: string;
    secondaryGtin?: string;
  })[];
}

export const HomepageHero = ({ slides }: HomepageHeroSlidesProps) => {
  const { hasLoggedPageView } = useContext(LogContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasLoggedViewEvent, setHasLoggedViewEvent] = useState<number[]>([]);
  const [slidePrimaryItems, setSlidePrimaryItems] = useState<{
    [slideIndex: number]: Product;
  }>({});
  const [slideSecondaryItems, setSlideSecondaryItems] = useState<{
    [slideIndex: number]: Product;
  }>({});

  useEffect(() => {
    if (hasLoggedPageView && slides.length) {
      const slide = slides[currentIndex];
      const logInternalPromotionViewEvent = (item: Product | null) => {
        logInternalPromotionView("", {
          promotionId: slide.internalPromotionAnalytics?.promotionId || "",
          promotionName: slide.internalPromotionAnalytics?.promotionName || "",
          creativeName: slide.internalPromotionAnalytics?.creativeName || "",
          creativeSlot: slide.internalPromotionAnalytics?.creativeSlot || "",
          ...(item ? { items: [item] } : {}),
        });
      };

      const getProductByGtin = async (
        gtin?: string
      ): Promise<Product | null> => {
        if (!gtin) {
          return null;
        }
        return client
          .query<ItemByGtinQuery, ItemByGtinQueryVariables>({
            query: ItemByGtinDocument,
            variables: { gtin: gtin },
          })
          .then(({ data }) => {
            if (data.itemByGtin) {
              return {
                _id: data.itemByGtin?.id || "",
                gtin: data.itemByGtin?.gtin || "",
                itemTitle: data.itemByGtin?.title?.toLocaleLowerCase() || "",
                itemPrice: data.itemByGtin?.price
                  ? data.itemByGtin?.price / 100
                  : 0,
                itemDesignerArtistBrandName:
                  data.itemByGtin?.itemDesignerArtistBrandName || "",
                itemDimensions: data.itemByGtin?.itemDimensions || "",
              } as Product;
            }
            return null;
          });
      };

      if (
        !hasLoggedViewEvent.includes(currentIndex) &&
        !!slide.internalPromotionAnalytics
      ) {
        Promise.all([
          getProductByGtin(slide.primaryGtin),
          getProductByGtin(slide.secondaryGtin),
        ]).then(([primaryItem, secondaryItem]) => {
          if (primaryItem) {
            setSlidePrimaryItems((prev) => {
              return {
                ...prev,
                [currentIndex]: primaryItem as Product,
              };
            });
          }
          if (secondaryItem) {
            setSlideSecondaryItems((prev) => {
              return {
                ...prev,
                [currentIndex]: secondaryItem as Product,
              };
            });
          }

          if (primaryItem && secondaryItem) {
            logInternalPromotionViewEvent(primaryItem);
            logInternalPromotionViewEvent(secondaryItem);
          } else if (primaryItem || secondaryItem) {
            logInternalPromotionViewEvent(primaryItem);
            logInternalPromotionViewEvent(secondaryItem);
          } else {
            logInternalPromotionViewEvent(null);
          }
          setHasLoggedViewEvent((prev) => {
            return [...prev, currentIndex];
          });
        });
      }
    }
  }, [currentIndex, hasLoggedViewEvent, slides, hasLoggedPageView]);

  const appendDots = (dots: ReactNode[]) => <div>{dots}</div>;

  const customPaging = (i: number) => {
    const { timelineProgressBar: timeline } = slides[i];
    const textClass = cx(st.text, i === currentIndex ? st.activeText : "");

    const renderPaging = () => {
      return (
        <p
          className={textClass}
          style={{
            color: slides[currentIndex].timelineProgressBar?.textColor?.hex,
          }}
        >
          {timeline?.text}
        </p>
      );
    };

    return (
      <div className={cx(st.paging, i === currentIndex && st.activePage)}>
        {i === currentIndex ? (
          <div
            className={cx(st.active)}
            style={{ background: timeline?.progressBarColor?.hex }}
          />
        ) : (
          <div className={st.inActive} />
        )}
        <div className={cx(st.textWrapper, st.text)}>{renderPaging()}</div>
      </div>
    );
  };

  const mainSliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    speed: 1000,
    autoplaySpeed: 4000,
    arrows: false,
    centerMode: true,
    centerPadding: "0",
    swipeToSlide: false,
    appendDots: appendDots,
    customPaging: customPaging,
    beforeChange: (_: number, next: number) => setCurrentIndex(next),
  };

  return (
    <div className={cx(st.container)}>
      <Slider className={st["slick-slider-custom"]} {...mainSliderSettings}>
        {slides.map((slide, index) => (
          <HomepageHeroSlide
            key={slide.header}
            backgroundImageUrl={slide.backgroundImage?.url || ""}
            mobileBackgroundImageUrl={
              slide.mobileBackgroundImage?.url ||
              slide.backgroundImage?.url ||
              ""
            }
            subHeader={slide.subHeader || ""}
            header={slide.header || ""}
            disclaimer={slide.disclaimer || ""}
            textColor={"black"}
            primaryButton={{
              text: slide.primaryCta?.text || "",
              color: slide.primaryCta?.backgroundColor?.hex || "",
              textColor: slide.primaryCta?.textColor?.hex || "",
              ctaUrl: slide.primaryCta?.url || "",
            }}
            secondaryButton={
              slide.secondaryCta
                ? {
                    text: slide.secondaryCta?.text || "",
                    color: slide.secondaryCta?.borderColor?.hex || "",
                    textColor: slide.secondaryCta?.textColor?.hex || "",
                    ctaUrl: slide.secondaryCta?.url || "",
                  }
                : undefined
            }
            imageUrl={slide.supportingAsset?.image?.url}
            embedVideoUrl={
              slide.supportingAsset?.isVideo &&
              slide.supportingAsset.youtubeVideoLink
            }
            textPosition={slide.textPosition || "left"}
            blanket={
              slide.blanket
                ? {
                    blanketType: slide.blanket.blanketType || "",
                    gradientType: slide.blanket.gradientType || "",
                  }
                : undefined
            }
            internalPromotionAnalytics={
              slide.internalPromotionAnalytics || undefined
            }
            primaryItem={slidePrimaryItems[index]}
            secondaryItem={slideSecondaryItems[index]}
          />
        ))}
      </Slider>
    </div>
  );
};
